import React, { useState } from "react"
import {
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Label,
  Input,
  Form,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap"
import { RiUserAddLine } from "react-icons/ri"
import { Formik, Field, ErrorMessage, Form as FormikForm } from "formik"
import * as Yup from "yup"
import API from "../../api/axios"
import { toast } from "react-toastify"

const PatientForm = ({ refresh }) => {
  const [open, setOpen] = useState(false)

  // initial values for Formik
  const initialValues = {
    fname: "",
    lname: "",
    phone: "",
    email: "",
    gender: "",
    addressLine1: "",
    addressLine2: "",
    city: "",
    state: "",
    zipCode: "",
    country: "",
  }

  // Yup validation schema
  const validationSchema = Yup.object({
    fname: Yup.string()
      .min(2, "First name must be at least 2 characters")
      .max(30, "First name cannot exceed 30 characters")
      .matches(/^[A-Za-z\s_-]+$/, "First name can only contain letters, spaces, hyphens (-), and underscores (_)")
    
      .required("First name is required"),
    lname: Yup.string()
      .min(2, "Last name must be at least 2 characters")
      .max(30, "Last name cannot exceed 30 characters")
      .matches(/^[A-Za-z\s_-]+$/, "Last name can only contain letters, spaces, hyphens (-), and underscores (_)")
      
      .required("Last name is required"),
    phone: Yup.string()
      .matches(/^\d{10}$/, "Phone must be 10 digits")
      .required("Phone is required"),
    email: Yup.string()
      .email("Invalid email format")
      .required("Email is required"),
    gender: Yup.string().required("Gender is required"),
    country: Yup.string().required("Country is required"),
    addressLine1: Yup.string().required("Address Line 1 is required"),
    city: Yup.string().required("City is required"),
    state: Yup.string().required("State is required"),
    zipCode: Yup.string()
      .matches(/^\d{5,6}$/, "Zip Code must be 5–6 digits")
      .required("Zip Code is required"),
  })

  const handleClickOpen = () => setOpen(true)
  const handleClose = () => setOpen(false)

  const handleSubmit = async (values, { resetForm }) => {
    try {
      const authToken = localStorage.getItem("authToken")
      await API.post("/patients", values, {
        headers: {
          Authorization: `Bearer ${authToken}`,
          "Content-Type": "application/json",
        },
      })
      toast.success("Patient added successfully!", { autoClose: 2000 })
      refresh()
      resetForm()
      handleClose()
    } catch (error) {
      console.error("Error:", error)
      toast.error("Error adding patient")
    }
  }

  return (
    <div>
      {/* Button to Open Modal */}
      <Button
        color="primary"
        onClick={handleClickOpen}
        className="float-right px-1 border py-2 mb-4 mt-2"
      >
        <RiUserAddLine size={25} /> Add Patient
      </Button>

      {/* Modal */}
      <Modal isOpen={open} toggle={handleClose}>
        <ModalHeader toggle={handleClose}>Add New Patient</ModalHeader>
        <ModalBody>
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
            validateOnChange={true}   // validate while typing
            validateOnBlur={true}     //  validate when leaving field
          >
            {({ isSubmitting }) => (
              <FormikForm>
                <Row>
                  <Col md="6">
                    <Label>First Name</Label>
                    <Field
                      as={Input}
                      type="text"
                      name="fname"
                    />
                    <ErrorMessage
                      name="fname"
                      component="small"
                      className="text-danger"
                    />
                  </Col>
                  <Col md="6">
                    <Label>Last Name</Label>
                    <Field as={Input} type="text" name="lname" />
                    <ErrorMessage
                      name="lname"
                      component="small"
                      className="text-danger"
                    />
                  </Col>
                </Row>

                <Row>
                  <Col md="6">
                    <Label>Phone</Label>
                    <Field as={Input} type="text" name="phone" />
                    <ErrorMessage
                      name="phone"
                      component="small"
                      className="text-danger"
                    />
                  </Col>
                  <Col md="6">
                    <Label>Email</Label>
                    <Field as={Input} type="email" name="email" />
                    <ErrorMessage
                      name="email"
                      component="small"
                      className="text-danger"
                    />
                  </Col>
                </Row>

                <Row>
                  <Col md="6">
                    <Label>Gender</Label>
                    <Field as={Input} type="select" name="gender">
                      <option value="">Select</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                    </Field>
                    <ErrorMessage
                      name="gender"
                      component="small"
                      className="text-danger"
                    />
                  </Col>
                  <Col md="6">
                    <Label>Country</Label>
                    <Field as={Input} type="text" name="country" />
                    <ErrorMessage
                      name="country"
                      component="small"
                      className="text-danger"
                    />
                  </Col>
                </Row>

                <Row>
                  <Col md="6">
                    <Label>Address Line 1</Label>
                    <Field as={Input} type="text" name="addressLine1" />
                    <ErrorMessage
                      name="addressLine1"
                      component="small"
                      className="text-danger"
                    />
                  </Col>
                  <Col md="6">
                    <Label>Address Line 2</Label>
                    <Field as={Input} type="text" name="addressLine2" />
                  </Col>
                </Row>

                <Row>
                  <Col md="4">
                    <Label>City</Label>
                    <Field as={Input} type="text" name="city" />
                    <ErrorMessage
                      name="city"
                      component="small"
                      className="text-danger"
                    />
                  </Col>
                  <Col md="4">
                    <Label>State</Label>
                    <Field as={Input} type="text" name="state" />
                    <ErrorMessage
                      name="state"
                      component="small"
                      className="text-danger"
                    />
                  </Col>
                  <Col md="4">
                    <Label>Zip Code</Label>
                    <Field as={Input} type="text" name="zipCode" />
                    <ErrorMessage
                      name="zipCode"
                      component="small"
                      className="text-danger"
                    />
                  </Col>
                </Row>

                <ModalFooter>
                  <Button color="secondary" onClick={handleClose}>
                    Cancel
                  </Button>
                  <Button color="primary" type="submit" disabled={isSubmitting}>
                    Submit
                  </Button>
                </ModalFooter>
              </FormikForm>
            )}
          </Formik>
        </ModalBody>
      </Modal>
    </div>
  )
}

export default PatientForm
